/* eslint-disable react/jsx-key */
import type { MouseEvent } from 'react';
import React from 'react';
import Hide from '@uber/base-icons/hide';
import Show from '@uber/base-icons/show';
import { styled, useStyletron } from 'baseui';
import { Checkbox } from 'baseui/checkbox';
import { CollapsiblePanel } from '../common/collapsible-panel/collapsible-panel.react';
import { LABEL_VALIDATIONS } from '../constants';
import type { LabelValidationT } from './types';
const BoundingBox = styled('div', props => ({
  borderWidth: '2px',
  borderStyle: 'solid',
  height: '18px',
  width: '18px',
  // @ts-expect-error todo(ts-migration) TS2339 Property '$color' does not exist on type 'Omit<{}, "$theme"> & { $theme: Theme; }'.
  borderColor: props.$color
}));
const CheckboxOverrides = {
  Root: {
    style: ({
      $theme
    }) => ({
      padding: $theme.sizing.scale300
    })
  },
  Label: {
    style: {
      fontWeight: 'normal'
    }
  }
};
type Tool = {
  description: string;
  color: string;
  title: string;
  count: number;
  type: string;
  hidden: boolean;
  max?: number;
  isRequired?: boolean;
};
type Props = {
  meta?: {
    [x: string]: string;
  };
  tools: Tool[];
  selectedIndex: number;
  onToolClick: (index: number) => void;
  width: number;
  onHideClick: {
    (index: number): void;
  };
  onShowClick: {
    (index: number): void;
  };
  invalidImage: boolean;
  labelValidation: LabelValidationT;
  onInvalidImageChange: (a: boolean) => void;
  onLabelValidation: (a: {
    isValid: boolean;
    reason: string;
  }) => void;
};
type ItemProps = {
  isSelected: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
  description: string;
  title: string;
  count: number;
  type: string;
  color: string;
  onHideClick: {
    (): void;
  };
  onShowClick: {
    (): void;
  };
  hidden: boolean;
  max?: number;
  isRequired?: boolean;
};
const ShownIcon = props => {
  const [, theme] = useStyletron();
  return <Show color={theme.colors.contentTertiary} {...props} />;
};
const HiddenIcon = props => {
  const [, theme] = useStyletron();
  return <Hide color={theme.colors.contentPrimary} {...props} />;
};

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const StyledItem = styled<'div', {
  $isSelected;
}>('div', ({
  $theme: {
    colors
  },
  $isSelected
}) => ({
  backgroundColor: $isSelected ? colors.borderAccentLight : colors.backgroundPrimary,
  borderTop: `1px solid ${colors.borderOpaque}`,
  color: colors.contentPrimary,
  display: 'flex',
  flexDirection: 'column'
}));
const Title = styled('span', {
  paddingLeft: '8px',
  fontSize: '1.1em'
});
const Content = styled('span', {
  display: 'flex',
  alignItems: 'center',
  padding: '12px 30px'
});
const Required = styled('span', ({
  $theme
}) => ({
  ...$theme.typography.LabelLarge,
  color: $theme.colors.negative
}));
const ContentContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}));
const ConstraintContainer = styled('div', {
  paddingLeft: '64px',
  marginTop: '-8px',
  marginBottom: '8px'
});
const ConstraintChip = styled('span', ({
  $theme
}) => ({
  ...$theme.typography.LabelXSmall,
  backgroundColor: $theme.colors.backgroundTertiary,
  color: $theme.colors.contentPrimary,
  borderRadius: $theme.sizing.scale300,
  padding: `${$theme.sizing.scale0} ${$theme.sizing.scale300}`,
  marginRight: 'auto',
  fontStyle: 'italic'
}));
const Item = (props: ItemProps) => {
  const onHideClick = e => {
    e.stopPropagation();
    props.onHideClick();
  };
  const onShowClick = e => {
    e.stopPropagation();
    props.onShowClick();
  };
  return <StyledItem onClick={props.onClick} $isSelected={props.isSelected}>
      <ContentContainer>
        <Content $style={{
        flex: '1'
      }}>
          {/* @ts-expect-error todo(ts-migration) TS2322 Type '{ $color: string; }' is not assignable to type 'IntrinsicAttributes & { $as?: "div"; } & { $style?: StyleObject | ((props: {}) => StyleObject); className?: string; } & Omit<DetailedHTMLProps<HTM... */}
          <BoundingBox $color={props.color} />
          <Title>
            {props.isRequired && <Required>*</Required>} {props.title}
          </Title>
        </Content>
        <Content>
          {props.count !== 0 && <span style={{
          paddingRight: '10px'
        }}>{props.count}</span>}
          {props.hidden ? <HiddenIcon onClick={onShowClick} /> : <ShownIcon onClick={onHideClick} />}
        </Content>
      </ContentContainer>
      {props.max !== undefined && <ConstraintContainer>
          <ConstraintChip>Max: {props.max}</ConstraintChip>
        </ConstraintContainer>}
    </StyledItem>;
};

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const MetaContainer = styled('div', ({
  $theme
}) => ({
  backgroundColor: $theme.colors.backgroundPrimary,
  color: $theme.colors.contentPrimary,
  padding: '10px 30px'
}));
const MetaKey = styled('div', {
  fontWeight: 'bold'
});
const MetaValue = styled('div', {
  padding: '0 0 5px 10px'
});

/* $FlowFixMe This comment suppresses an error found when enabling type
 * checking of the legacy common/shared/ packages in the monorepo. To view the
 * error, delete this comment and run Flow. */
const PanelContainer = styled<'div', {
  $width;
}>('div', ({
  $theme,
  $width
}) => ({
  width: `${$width}px`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  border: `1px solid ${$theme.colors.borderOpaque}`
}));

/*
  WIP Panel, this currently has very little styling...
*/
export class Panel extends React.Component<Props> {
  static defaultProps = {
    tools: [],
    onToolClick: () => {},
    selectedIndex: -1,
    width: 300,
    backgroundColor: '#272D3B'
  };
  renderItem = (tool: Tool, index: number) => {
    return <Item key={tool.title} description={tool.description} title={tool.title} isSelected={index === this.props.selectedIndex} count={tool.count} onClick={() => this.props.onToolClick(index)} type={tool.type} color={tool.color} onHideClick={() => this.props.onHideClick(index)} onShowClick={() => this.props.onShowClick(index)} hidden={tool.hidden} max={tool.max} isRequired={tool.isRequired} />;
  };
  renderMeta = (meta: {
    [x: string]: string;
  }) => {
    const keys = Object.keys(meta);
    if (!keys.length) {
      return null;
    }
    return <MetaContainer>
        {keys.map((key: string) => {
        return <div>
              <MetaKey>{`${key}:`}</MetaKey> <MetaValue>{meta[key]}</MetaValue>
            </div>;
      })}
      </MetaContainer>;
  };
  render() {
    const {
      width,
      invalidImage,
      labelValidation,
      onInvalidImageChange,
      onLabelValidation
    } = this.props;
    const MetaData = this.renderMeta(this.props.meta || {});
    const showRequiredLabel = this.props.tools.some(tool => tool.isRequired);
    return <PanelContainer $width={width}>
        <CollapsiblePanel title={'All Label Types'}>
          {this.props.tools.map(this.renderItem)}
        </CollapsiblePanel>
        <CollapsiblePanel title={'Additional options'}>
          <Checkbox checked={invalidImage} onChange={e => onInvalidImageChange(e.target.checked)} overrides={CheckboxOverrides}>
            {"Image can't be labelled"}
          </Checkbox>
          {showRequiredLabel && <Checkbox checked={labelValidation.reasons.includes(LABEL_VALIDATIONS.REQUIRED_LABELS_NOT_AVAILABLE)} onChange={e => onLabelValidation({
          isValid: e.target.checked === false,
          reason: LABEL_VALIDATIONS.REQUIRED_LABELS_NOT_AVAILABLE
        })} overrides={{
          ...CheckboxOverrides,
          Input: {
            props: {
              'data-testid': 'required-label-chk'
            }
          }
        }}>
              {'Required Labels not available'}
            </Checkbox>}
        </CollapsiblePanel>
        {MetaData && <CollapsiblePanel title={'Additional Information'}>{MetaData}</CollapsiblePanel>}
      </PanelContainer>;
  }
}